import React, { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import { Button } from "@mui/material"
import { locale, loadMessages } from "devextreme/localization";
import { FormattedMessage, useIntl, IntlShape } from 'react-intl';
import { DataGrid, Column, Export, GroupPanel, Grouping, FilterRow, Pager, Paging, StateStoring, HeaderFilter, Selection, Sorting } from 'devextreme-react/data-grid';
import {
    MultiLoggerColumnData

} from './MinimumNightFlowFunctions'
import moment from 'moment'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
interface Props {
    show: boolean
    toggleTrendModal: () => void
    data: MultiLoggerColumnData[]
}

interface WeeklyData {
    startDate: Date
    endDate: Date
    totalFlow: number
    minFlow: number
    minFlowTimeStamp: Date
    maxFlow: number
    maxFlowTimeStamp: Date
}

interface OutputTableData {
    weekCommencing:  Date
    weekEnding: Date
    weeklyMaxFlow: number
    weeklyMaxFlowTimeStamp: Date
    weeklyMinFlow: number
    weeklyMinFlowTimeStamp: Date
    weeklyAverageFlow: number
    weeklyPercentageChange:number
}

const MinimumNightFlowTrend: React.FC<Props> = ({ show, toggleTrendModal, data }) => {

    const intl: IntlShape = useIntl()


    const [tableData, setTableData] = useState<OutputTableData[]>([])


    useEffect(() => {
        const outputTableData:OutputTableData[] = []
        const weeklyData: WeeklyData[] = []
        
        const exclude = ['day', 'average', 'timeOfReading']
        if (data.length > 0) {
            const firstDay = moment(data[0].day).format('ddd')
            data.forEach((d: MultiLoggerColumnData) => {
                let dailyTotal = 0
                Object.keys(d).forEach(key => {
                    if (!exclude.includes(key)) {
                        dailyTotal += d[key] as number
                    }
                })
                if (moment(d.day).format('ddd') === firstDay) {
                    weeklyData.push({
                        startDate: d.day,
                        endDate: moment(d.day).add(6, 'days').toDate(),
                        totalFlow: dailyTotal,
                        maxFlow: dailyTotal,
                        maxFlowTimeStamp: d.timeOfReading,
                        minFlow: dailyTotal,
                        minFlowTimeStamp: d.timeOfReading
                    })
                } else {
                    weeklyData[weeklyData.length - 1].totalFlow = weeklyData[weeklyData.length - 1].totalFlow + dailyTotal
                    if (dailyTotal > weeklyData[weeklyData.length - 1].maxFlow) {
                        weeklyData[weeklyData.length - 1].maxFlow = dailyTotal
                        weeklyData[weeklyData.length - 1].maxFlowTimeStamp = d.timeOfReading
                    }
                    if (dailyTotal < weeklyData[weeklyData.length - 1].minFlow) {
                        weeklyData[weeklyData.length - 1].minFlow = dailyTotal
                        weeklyData[weeklyData.length - 1].minFlowTimeStamp = d.timeOfReading
                    }
                }
            })
            weeklyData[weeklyData.length - 1].endDate = data[data.length - 1].day
            weeklyData.forEach(d => {

                const days = moment(d.endDate).diff(moment(d.startDate), 'days')
                const weeklyAvg = d.totalFlow / (days + 1)
                const previousWeeklyAverage = outputTableData[outputTableData.length - 1]?.weeklyAverageFlow
                
                outputTableData.push({
                    weekCommencing: d.startDate,
                    weekEnding: d.endDate,
                    weeklyMaxFlow: parseFloat(d.maxFlow.toFixed(3)),
                    weeklyMaxFlowTimeStamp: d.maxFlowTimeStamp,
                    weeklyMinFlow: parseFloat(d.minFlow.toFixed(3)),
                    weeklyMinFlowTimeStamp: d.minFlowTimeStamp,
                    weeklyAverageFlow: parseFloat(weeklyAvg.toFixed(3)),
                    weeklyPercentageChange: previousWeeklyAverage
                        ? parseFloat((((weeklyAvg - previousWeeklyAverage) / previousWeeklyAverage) * 100).toFixed(2))
                    : 0
                })
            })
            console.log(outputTableData)
            setTableData(outputTableData)
        }
       
    }, [data])


    const renderPercentageChange = (data): React.ReactNode => {
        if (data.data.weeklyPercentageChange > 0) {
            return (
                <div style={{ display: 'flex' }}>
                    {data.data.weeklyPercentageChange && <ArrowDropUpIcon color={data.data.weeklyPercentageChange > 15 ? "error" : "warning"} />}
                    <p
                        style={{ color: data.data.weeklyPercentageChange > 15 ? 'red' : 'orange' }}
                    >
                        <b>{data.data.weeklyPercentageChange ? `${data.data.weeklyPercentageChange}%` : ''}</b>
                    </p>
                </div>
            )
        } else if (data.data.weeklyPercentageChange < 0) {
            return (
                <div style={{ display: 'flex' }}>
                    <ArrowDropDownIcon color='success' />
                    <p
                        style={{ color: 'green' }}
                    >
                        <b>{data.data.weeklyPercentageChange ? `${data.data.weeklyPercentageChange}%` : ''}</b>
                    </p>
                </div>
            )
        }
    }


    return (
        <Modal
            show={show}
            style={{
                position: 'absolute',
                top: 300,
                zIndex: 10000000000,
                left: '2.5%'
            }}
            className="modal-95w"
        >
            <Modal.Header>
                <Modal.Title><FormattedMessage id="Minimum Night Flow Trends" />
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DataGrid
                    dataSource={tableData}
                >
                    <FilterRow visible={true} />
                    <HeaderFilter visible={true} search={{ enabled: true }} />
                    <Column
                    dataType="date"
                        cellRender={(data): React.ReactNode =>
                            <p>{`${moment(data.data.weekCommencing).format('DD MMM YYYY')} - ${moment(data.data.weekEnding).format('DD MMM YYYY')}`}</p>
                        }
                        caption={intl.formatMessage({id: 'Date Range'})}
                    />
                    <Column dataField="weeklyMinFlow" caption={intl.formatMessage({ id: 'Min Flow (l/s)' })} />
                    <Column dataField="weeklyMinFlowTimeStamp" caption={intl.formatMessage({ id: 'Date / Time of Minimum Flow' })} cellRender={(data): React.ReactNode => <p>{moment(data.data.weeklyMinFlowTimeStamp).format('DD MMM YYYY HH:mm:ss')}</p>} />
                    <Column dataField="weeklyMaxFlow" caption={intl.formatMessage({ id: 'Max Flow (l/s)' })} />
                    <Column dataField="weeklyMaxFlowTimeStamp" caption={intl.formatMessage({ id: 'Date / Time of Maximum Flow' })} cellRender={(data): React.ReactNode => <p>{moment(data.data.weeklyMaxFlowTimeStamp).format('DD MMM YYYY HH:mm:ss')}</p>} />
                    <Column dataField="weeklyAverageFlow" caption={intl.formatMessage({ id: 'Average Flow (l/s)' })} />
                    <Column dataField="weeklyPercentageChange" caption={intl.formatMessage({ id: '% Change in Average Flow from Previous Week' })} cellRender={(data) => renderPercentageChange(data)} />

                </DataGrid>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="contained" color="error" onClick={(): void => toggleTrendModal()} ><FormattedMessage id="Close" /></Button>
            </Modal.Footer>
        </Modal>
    )
}
export default MinimumNightFlowTrend